import type { CourseStructureItem } from '@vedalib/editor/lib/elements'
import { CollapseIconPosition, ProgressIconPosition } from '@vedalib/editor/lib/elements'
import cn from 'classnames'
import { useMemo } from 'react'

import { useCourseSidebarData } from 'components/Editor/cource/controls/CourseSidebar/context/selectors'
import StructureNodeCurseStyled from 'components/Editor/cource/controls/CourseSidebar/styled/StructureNodeCurseStyled'
import { getIconData, sectionConfig } from 'components/Editor/cource/controls/CourseSidebar/utils'
import ScreenReaderOnlyText from 'components/Editor/styled/ScreenReaderOnlyText'
import type { ISectionTreeNode } from 'components/sections/ProjectStructure/utils'
import CollapseIcon from 'components/uiKit/Collapse/CollapseIcon'
import { useProjectT } from 'services/ProjectLabels'
import { SectionTypeEnum } from 'services/Store/Project/enums'
import { getEnumLabel } from 'utils/enum'

import SectionStatus from '../SectionStatus/SectionStatus'
import { StructureItemCourse } from '../StructureItemCourse'
import * as s from './StructureNodeCourse.module.scss'

export const LEVEL_INDENT = 12
interface ITreeNodeProps {
  toggle?(active?: boolean): void
  open: boolean
  node: ISectionTreeNode<CourseStructureItem>
  onClick?: () => void
  collapsedListId?: string
}

const StructureNodeCourse: React.FC<ITreeNodeProps> = ({
  open,
  node,
  onClick,
  toggle,
  collapsedListId,
}) => {
  const pt = useProjectT()
  const { item } = node
  const { getName, countDetails } = sectionConfig[item.type]
  const { styles, font, getActiveSection, getDisabledSection } = useCourseSidebarData()
  const active = getActiveSection(item)
  const nestedOffset = item.lvl * LEVEL_INDENT
  const disabled = useMemo(() => getDisabledSection(item.id), [getDisabledSection, item.id])
  const showDescription = styles?.value.showDescription && item.type !== SectionTypeEnum.landing
  const iconPosition = [CollapseIconPosition.right, CollapseIconPosition.rightOutside].includes(
    styles.collapse.collapsePosition as CollapseIconPosition,
  )
    ? 'right'
    : 'left'
  const customIconCollapse = open
    ? styles.collapseIcon.backgroundOpen
    : styles.collapseIcon.backgroundImage

  const collapseIcon = (
    <div className={s.collapseIcon} aria-hidden>
      {item.isChapter &&
        toggle &&
        styles.collapse.collapsePosition !== CollapseIconPosition.none &&
        (customIconCollapse ? (
          <div className={s.customIcon} style={{ backgroundImage: customIconCollapse }} />
        ) : (
          <CollapseIcon iconPosition={iconPosition} open={open} />
        ))}
    </div>
  )

  const onClickHandler = () => {
    if (disabled) {
      return
    }

    if (item.isChapter) {
      toggle?.()
    } else {
      onClick?.()
    }
  }

  const collapseOffset = styles?.margins.offsetFromCollapse
  const statusOffset = styles?.margins.offsetFromProgress
  const { showIcon } = getIconData({ type: item.type, styles, font, disabled, nestedOffset })

  const itemContent = (
    <>
      {/* row1 */}
      {active && <ScreenReaderOnlyText>Current page.</ScreenReaderOnlyText>}
      {styles.collapse.collapsePosition === CollapseIconPosition.leftOutside && (
        <div className={s.collapse} style={{ marginRight: collapseOffset }}>
          {collapseIcon}
        </div>
      )}
      {styles.verticalProgress.progressPosition === ProgressIconPosition.left && (
        <div className={s.sectionStatus} style={{ marginRight: statusOffset }}>
          <SectionStatus disabled={disabled} node={node} open={open} />
        </div>
      )}
      {styles.collapse.collapsePosition === CollapseIconPosition.left && (
        <div className={s.nodeIndent} style={{ marginRight: collapseOffset }}>
          {collapseIcon}
        </div>
      )}
      <StructureItemCourse disabled={disabled} item={node.item} nestedOffset={nestedOffset} />
      {styles.collapse.collapsePosition === CollapseIconPosition.right && (
        <div className={cn(s.nodeIndent, s.collapse)} style={{ marginLeft: collapseOffset }}>
          {collapseIcon}
        </div>
      )}
      {styles.verticalProgress.progressPosition === ProgressIconPosition.right && (
        <div className={s.sectionStatus} style={{ marginLeft: statusOffset }}>
          <SectionStatus disabled={disabled} node={node} open={open} />
        </div>
      )}
      {styles.collapse.collapsePosition === CollapseIconPosition.rightOutside && (
        <div className={cn(s.nodeIndent, s.collapse)} style={{ marginLeft: collapseOffset }}>
          {collapseIcon}
        </div>
      )}
      {/* row2 */}
      {showDescription && (
        <div className={s.description}>
          <div style={font?.description}>
            <span>{getName(pt) || getEnumLabel('SectionTypeEnum', item.type)}</span>
            <span className={s.separator}>&#45;</span>
            <span>{countDetails(item, pt, node.children?.length || 0)}</span>
          </div>
        </div>
      )}
    </>
  )

  if (item.isChapter) {
    return (
      <StructureNodeCurseStyled
        $active={active}
        $disabled={disabled}
        $isChapter={item.isChapter}
        $open={open}
        $showIcon={showIcon}
        $styles={styles}
        aria-controls={collapsedListId}
        aria-expanded={open}
        as='button'
        disabled={disabled}
        onClick={onClickHandler}
      >
        {itemContent}
      </StructureNodeCurseStyled>
    )
  }

  return (
    <li>
      <StructureNodeCurseStyled
        $active={active}
        $disabled={disabled}
        $isChapter={item.isChapter}
        $open={open}
        $showIcon={showIcon}
        $styles={styles}
        aria-disabled={disabled}
        onClick={onClickHandler}
        onKeyDown={(e: React.KeyboardEvent) => {
          if (e.key === 'Enter') {
            onClickHandler()
          }
        }}
        tabIndex={disabled ? -1 : 0}
      >
        {itemContent}
      </StructureNodeCurseStyled>
    </li>
  )
}

export default StructureNodeCourse
